<script setup>
/**
 * Leaf body for a saved view (CustomFilter) in any sidebar surface -
 * conversations, tickets or contacts: the usual icon + label plus a ⋯ manage
 * menu with share/unshare and delete. Rendered inside SidebarGroupLeaf's
 * router-link, so every menu interaction stops propagation to keep the link
 * from navigating.
 */
import { ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';

const props = defineProps({
  label: { type: String, required: true },
  icon: { type: [String, Object], default: null },
  badgeCount: { type: [Number, String], default: 0 },
  badgeCountLabelKey: { type: String, default: undefined },
  shared: { type: Boolean, default: false },
  canManage: { type: Boolean, default: false },
  canShare: { type: Boolean, default: false },
  onDelete: { type: Function, default: () => {} },
  onToggleShared: { type: Function, default: () => {} },
});

// `active` also arrives from SidebarGroupLeaf; it lands in $attrs since this
// leaf body has no use for it, and the multi-root template cannot inherit it
// anyway.
defineOptions({ inheritAttrs: false });

const { t } = useI18n();

const menuOpen = ref(false);
const deleteDialogRef = ref(null);

const closeMenu = () => {
  menuOpen.value = false;
};

const toggleMenu = event => {
  event.preventDefault();
  event.stopPropagation();
  menuOpen.value = !menuOpen.value;
};

const runAction = (event, action) => {
  event.preventDefault();
  event.stopPropagation();
  closeMenu();
  action();
};

// Deleting a saved view is not undoable, so the menu item only opens the
// confirmation; the delete itself runs once the dialog resolves.
const requestDelete = async () => {
  const confirmed = await deleteDialogRef.value.showConfirmation();
  if (confirmed) props.onDelete();
};
</script>

<template>
  <span v-if="icon" class="size-4 grid place-content-center rounded-full">
    <Icon :icon="icon" class="size-4 inline-block" />
  </span>
  <div class="flex-1 truncate min-w-0 text-sm">{{ label }}</div>
  <SidebarUnreadBadge :count="badgeCount" :label-key="badgeCountLabelKey" />
  <div
    v-if="canManage"
    v-on-click-outside="closeMenu"
    class="relative shrink-0"
  >
    <button
      type="button"
      data-testid="view-menu-trigger"
      :aria-label="t('SIDEBAR.VIEW_MENU.MANAGE')"
      aria-haspopup="menu"
      :aria-expanded="menuOpen"
      class="grid rounded size-6 place-items-center text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
      :class="menuOpen ? 'visible' : 'invisible group-hover:visible'"
      @click="toggleMenu"
    >
      <span class="i-lucide-ellipsis size-4" />
    </button>
    <div
      v-if="menuOpen"
      role="menu"
      class="absolute z-30 w-40 p-1 rounded-xl top-7 end-0 bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-container shadow-lg"
    >
      <button
        v-if="canShare"
        type="button"
        role="menuitem"
        data-testid="view-menu-share"
        class="flex items-center w-full h-8 gap-2 px-2.5 text-sm rounded-lg text-start text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
        @click="runAction($event, onToggleShared)"
      >
        <span
          :class="shared ? 'i-lucide-user-round' : 'i-lucide-users'"
          class="size-3.5"
        />
        {{
          shared ? t('SIDEBAR.VIEW_MENU.UNSHARE') : t('SIDEBAR.VIEW_MENU.SHARE')
        }}
      </button>
      <button
        type="button"
        role="menuitem"
        data-testid="view-menu-delete"
        class="flex items-center w-full h-8 gap-2 px-2.5 text-sm rounded-lg text-start text-n-ruby-11 hover:bg-n-ruby-3"
        @click="runAction($event, requestDelete)"
      >
        <span class="i-lucide-trash-2 size-3.5" />
        {{ t('SIDEBAR.VIEW_MENU.DELETE') }}
      </button>
    </div>
    <ConfirmDialog
      ref="deleteDialogRef"
      :title="t('SIDEBAR.VIEW_MENU.DELETE_CONFIRM.TITLE')"
      :description="
        t('SIDEBAR.VIEW_MENU.DELETE_CONFIRM.DESCRIPTION', { name: label })
      "
      :confirm-button-label="t('SIDEBAR.VIEW_MENU.DELETE_CONFIRM.CONFIRM')"
    />
  </div>
</template>
